import React, { useState } from 'react';
import '../styles/Page.css';
import '../styles/Strategy.css';
import { ChevronDown, ChevronUp, Download } from 'lucide-react';

// Import PDF documents
import investmentHorizonsPDF from '../assets/documents/DONCTOMemoINVESTMENTHORIZONSCHARTS03Apr20241.pdf';
import infosuperiorityPDF from '../assets/documents/InformationSuperiorityVision2.0Signed.pdf';
import aiActionPlanPDF from '../assets/documents/Americas-AI-Action-Plan.pdf';
import adaptiveRoadmapsPDF from '../assets/documents/DONCIORoadmapsACDCFinalEDC.pdf';
import iakPlaybookPDF from '../assets/documents/DON_CTO_IAK_v10.pdf';
import iakMemoPDF from '../assets/documents/DONCTOIAKMemo_v3_withsignature.pdf';
import warfightingAcquisitionPDF from '../assets/documents/TRANSFORMING-THE-DEFENSE-ACQUISITION-SYSTEM.pdf';
import acquisitionTransformationPDF from '../assets/documents/Acquisition Transformation Strategy.pdf';

function Strategy() {
  const [expandedSections, setExpandedSections] = useState({
    federal: false,
    stateCommercial: false,
    documents: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>ALIGN℠ Transformation Framework</h1>
        <p>Synchronizing strategy, operations, data, and technology into a unified system of execution</p>
      </div>

      <div className="page-content">
        <section className="content-section">
          <p>
            ALIGN℠ is One Aligned's transformation framework for synchronizing strategy, operations, data, and technology into a single, unified system of execution. It provides a clear, repeatable way to turn vision into results—whether the organization is a Federal program office, a Navy portfolio, a State agency, or a commercial enterprise.
          </p>
          <p>
            The framework is intentionally designed to support modern Acquisition Reform, digital transformation, and AI-enabled operational improvement, using a common language for value, risk, architecture alignment, and measurable outcomes.
          </p>
        </section>

        <section className="content-section">
          <div 
            className="collapsible-header" 
            onClick={() => toggleSection('federal')}
            style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <h2>ALIGN℠ for Federal & Defense Acquisition Reform</h2>
            {expandedSections.federal ? <ChevronUp size={24} color="#4dd0e1" /> : <ChevronDown size={24} color="#4dd0e1" />}
          </div>
          <div className={`collapsible-content ${expandedSections.federal ? 'expanded' : 'collapsed'}`}>
          <p>The Federal and Defense environment is undergoing a significant shift:</p>
          <ul>
            <li>From program-centric delivery to portfolio-based acquisition</li>
            <li>From isolated requirements to mission-thread and mission-outcome alignment</li>
            <li>From static documentation to data-driven, AI-ready architectures</li>
            <li>From compliance-heavy processes to modern service delivery and rapid adoption</li>
          </ul>
          <p>
            ALIGN℠ provides a structured, repeatable method to operationalize this new direction across programs, portfolios, partners, and vendors.
          </p>

          <h3>How ALIGN℠ Supports DoD & DoN Priorities</h3>
          <p>ALIGN℠ is mapped directly to emerging guidance such as:</p>
          <ul>
            <li>Warfighting Acquisition System and Portfolio Acquisition Executives (PAEs)</li>
            <li>DON CIO's Innovation Adoption Kit (IAK) and Adaptive Roadmaps Framework</li>
            <li>Information Superiority Vision (ISV) and modern service delivery principles</li>
            <li>Data integration, reference architecture alignment, and Cyber/RMF pre-work</li>
          </ul>

          <h3>What ALIGN℠ Enables for Federal Organizations</h3>
          <ul>
            <li>Portfolio-aligned roadmaps that connect mission problems to funded solutions</li>
            <li>Use of mission threads and data flows to prioritize investments and divestments</li>
            <li>Alignment of AI/ML and analytics tools with approved reference architectures and security patterns</li>
            <li>Definition of measurable outcome metrics tied to readiness, affordability, and speed-to-field</li>
            <li>Repeatable governance that increases transparency and decision velocity across portfolios</li>
          </ul>
          </div>
        </section>

        <section className="content-section">
          <div 
            className="collapsible-header" 
            onClick={() => toggleSection('stateCommercial')}
            style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <h2>ALIGN℠ for State Government & Commercial Organizations</h2>
            {expandedSections.stateCommercial ? <ChevronUp size={24} color="#4dd0e1" /> : <ChevronDown size={24} color="#4dd0e1" />}
          </div>
          <div className={`collapsible-content ${expandedSections.stateCommercial ? 'expanded' : 'collapsed'}`}>
          <p>
            State agencies and commercial enterprises face many of the same challenges as Federal programs: aging infrastructure, staffing shortages, cybersecurity risk, budget pressure, and rising expectations for modern digital services.
          </p>
          <p>
            ALIGN℠ brings disciplined, mission-aligned transformation—sharpened in demanding Federal environments—to help leaders in every sector achieve measurable results.
          </p>

          <h3>What ALIGN℠ Enables in State & Commercial Settings</h3>
          <p>We help leadership teams:</p>
          <ul>
            <li>Clarify mission, customer, and regulatory requirements</li>
            <li>Map critical value streams and service journeys</li>
            <li>Simplify processes and integrate data to remove friction</li>
            <li>Align technology investments directly to business outcomes</li>
            <li>Establish KPIs and feedback loops that sustain improvement over time</li>
          </ul>

          <h3>Common Use Cases</h3>
          <ul>
            <li>Modernizing core business systems and workflows without losing service continuity</li>
            <li>Aligning data, analytics, and AI pilots with real outcomes—not just experiments</li>
            <li>Transforming siloed functions into cross-functional, outcome-focused teams</li>
            <li>Giving boards and executives clear line-of-sight from strategy → execution → results</li>
          </ul>
          </div>
        </section>

        <section className="content-section">
          <h2>One Framework, Multiple Domains</h2>
          <p>
            Across all environments—Federal, Defense, State, and Commercial—the goal is the same:
          </p>
          <p>
            <strong>Alignment between strategy, people, processes, data, and technology so that every initiative meaningfully contributes to mission and business outcomes.</strong>
          </p>
          <p>ALIGN℠ is designed to be:</p>
          <ul>
            <li>Scalable and repeatable</li>
            <li>Architecture-aligned and AI-enabled</li>
            <li>Portfolio-ready and governance-friendly</li>
            <li>Outcome-driven and transparent</li>
          </ul>
          <p>
            Organizations use ALIGN℠ to navigate complexity, select the right investments, accelerate transformation, and deliver results with confidence.
          </p>
          <p>
            If you would like a tailored briefing or workshop on how ALIGN℠ can support your mission or business, please contact us through our homepage or reach out directly to One Aligned℠.
          </p>
        </section>

        <section className="content-section">
          <div 
            className="collapsible-header" 
            onClick={() => toggleSection('documents')}
            style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <h2>Policy Foundations & Source Documents</h2>
            {expandedSections.documents ? <ChevronUp size={24} color="#4dd0e1" /> : <ChevronDown size={24} color="#4dd0e1" />}
          </div>
          <div className={`collapsible-content ${expandedSections.documents ? 'expanded' : 'collapsed'}`}>
          <p>
            ALIGN℠ is grounded in the same policy stack now reshaping U.S. defense acquisition and digital transformation — from the Department of the Navy's Information Superiority Vision and Innovation Adoption Kit (IAK) to the Department of War's Warfighting Acquisition System and Acquisition Transformation Strategy.
          </p>
          <p>
            To see the full list of source documents, including dates, file names, and why each matters to Acquisition Reform and portfolio-based execution, see below:
          </p>

          <h3 style={{ marginTop: '30px' }}>Acquisition Reform Source Documents</h3>
          <p>
            The ALIGN℠ Transformation Framework is deliberately built on the same policy and strategy stack that is reshaping U.S. defense acquisition — from the Department of the Navy's digital and portfolio guidance to the Department of War's Warfighting Acquisition System (WAS) and Acquisition Transformation Strategy.
          </p>
          <p>
            The documents below are listed in chronological order of public release to show how the reform story has been building over time. Each description explains why the document matters and how it connects to portfolio-based acquisition, data-centric operations, AI, and industrial-base resilience.
          </p>

          <h3>Foundational DON Digital & Portfolio Guidance</h3>
          
          <h4>1. DON CTO Memo – "Investment Horizons Charts" (3 April 2024)</h4>
          <p>
            <strong>Why it matters:</strong> This memo introduces Investment Horizons as a structured way to visualize and manage capabilities from Horizon 3 (emerging) through Horizon 0 (divesting). It is a backbone for portfolio-based decisions, divestment of legacy IT, and reinvestment in modern, mission-aligned capabilities. This is exactly the kind of disciplined portfolio management the Warfighting Acquisition System (WAS) now expects from Portfolio Acquisition Executives (PAEs) and their teams.
          </p>
          <p><strong>File name:</strong> DONCTOMemoINVESTMENTHORIZONSCHARTS03Apr20241.pdf</p>
          <p><strong>Date:</strong> 3 April 2024</p>
          <button className="download-button" onClick={() => window.open(investmentHorizonsPDF, '_blank')}>
            <Download size={18} />
            <span>Download: Investment Horizons Charts Memo (PDF)</span>
          </button>

          <h4>2. Department of the Navy – Information Superiority Vision (ISV) 2.0 (16 August 2024)</h4>
          <p>
            <strong>Why it matters:</strong> Information Superiority Vision 2.0 sets the Department of the Navy end-state of delivering the right information securely, from anywhere to anywhere, to support warfighters at the speed of mission. It defines the Optimize, Secure, Decide pillars that underpin modern service delivery and data-centric operations. ISV 2.0 aligns directly with WAS priorities for resilient, data-driven warfighting and provides the digital context in which acquisition portfolios must now operate.
          </p>
          <p><strong>File name:</strong> InformationSuperiorityVision2.0Signed.pdf</p>
          <p><strong>Date:</strong> 16 August 2024</p>
          <button className="download-button" onClick={() => window.open(infosuperiorityPDF, '_blank')}>
            <Download size={18} />
            <span>Download: Information Superiority Vision 2.0 (PDF)</span>
          </button>

          <h3>National AI Posture & DON Portfolio Execution</h3>
          
          <h4>3. White House – "America's AI Action Plan" (23 July 2025)</h4>
          <p>
            <strong>Why it matters:</strong> America's AI Action Plan establishes a national-level roadmap for accelerating AI innovation, building AI infrastructure, and ensuring U.S. leadership in AI-enabled defense and industry. It provides the strategic context for using AI to transform acquisition, industrial-base analytics, and mission outcomes. The same AI-centric posture is reflected in the Warfighting Acquisition System and the Acquisition Transformation Strategy, and is operationalized at the portfolio level by frameworks like ALIGN℠.
          </p>
          <p><strong>File name:</strong> Americas-AI-Action-Plan.pdf</p>
          <p><strong>Date:</strong> 23 July 2025</p>
          <button className="download-button" onClick={() => window.open(aiActionPlanPDF, '_blank')}>
            <Download size={18} />
            <span>Download: America's AI Action Plan (PDF)</span>
          </button>

          <h4>4. DON CIO Memo – "Use of Adaptive Roadmaps for Strategic Investment Decisions" (31 July 2025)</h4>
          <p>
            <strong>Why it matters:</strong> This memo defines Adaptive Roadmaps as a best practice for portfolio planning and execution, tying together Investment Horizons, execution schedules, and DOTMLPF-P campaign elements. It shows how to connect mission problems to funded solutions over time. This is a direct precursor to how WAS expects PAEs and portfolio teams to steer capability portfolios, manage risk, and synchronize modernization with warfighting needs.
          </p>
          <p><strong>File name:</strong> DONCIORoadmapsACDCFinalEDC.pdf</p>
          <p><strong>Date:</strong> 31 July 2025</p>
          <button className="download-button" onClick={() => window.open(adaptiveRoadmapsPDF, '_blank')}>
            <Download size={18} />
            <span>Download: Adaptive Roadmaps Memo (PDF)</span>
          </button>

          <h3>Innovation Adoption Kit (IAK) – Systematic Technology Integration</h3>
          
          <h4>5. DON CTO – Innovation Adoption Kit (IAK) v1.0 (Companion Playbook, 2025)</h4>
          <p>
            <strong>Why it matters:</strong> The Innovation Adoption Kit (IAK) consolidates Investment Horizons, Adaptive Roadmaps, Modern Service Delivery, and World-Class Alignment Metrics (WAMs) into a single lifecycle for moving capabilities from tech scouting and pilots into scaled enterprise services — and retiring obsolete ones. It provides a repeatable "from pilot to production" model for AI, software, and digital capabilities that portfolios can adopt. ALIGN℠ aligns directly with the IAK to help leaders operationalize these patterns within their organizations.
          </p>
          <p><strong>File name:</strong> DON_CTO_IAK_v10.pdf</p>
          <p><strong>Date:</strong> 2025 (aligned to the IAK memo below)</p>
          <button className="download-button" onClick={() => window.open(iakPlaybookPDF, '_blank')}>
            <Download size={18} />
            <span>Download: Innovation Adoption Kit (IAK) v1.0 (PDF)</span>
          </button>

          <h4>6. DON CTO Memo – "Innovation Adoption Kit (IAK) – Systematic Framework for Technology Integration" (October 2025)</h4>
          <p>
            <strong>Why it matters:</strong> This memo formally designates the IAK as the Department of the Navy's system-wide framework for evaluating, piloting, scaling, and divesting technologies. It codifies structured approaches such as Investment Horizons, Structured Piloting, Structured Challenges, Structured Divestment, and WAMs. The governance signals in this memo mirror the Department of War's expectations under WAS: use disciplined, portfolio-level methods to adopt innovation at speed, with clear alignment to mission outcomes and risk management.
          </p>
          <p><strong>File name:</strong> DONCTOIAKMemo_v3_withsignature.pdf</p>
          <p><strong>Date:</strong> October 2025</p>
          <button className="download-button" onClick={() => window.open(iakMemoPDF, '_blank')}>
            <Download size={18} />
            <span>Download: Innovation Adoption Kit Memo (PDF)</span>
          </button>

          <h3>Department of War – Warfighting Acquisition System & Strategy</h3>
          
          <h4>7. Secretary of War Memo – "Transforming the Defense Acquisition System into the Warfighting Acquisition System" (7 November 2025)</h4>
          <p>
            <strong>Why it matters:</strong> This memo is the cornerstone directive that formally establishes the Warfighting Acquisition System (WAS) and creates Portfolio Acquisition Executives (PAEs). It shifts the Department of War from program-by-program oversight to portfolio-based accountability, demanding faster delivery, flexible pathways (including OTAs and commercial solutions), and stronger ties between acquisition decisions, warfighting timelines, and industrial-base health. ALIGN℠ is designed to help PAEs, program offices, and industry partners operate effectively within this new construct.
          </p>
          <p><strong>File name:</strong> TRANSFORMING-THE-DEFENSE-ACQUISITION-SYSTEM.pdf</p>
          <p><strong>Date:</strong> 7 November 2025</p>
          <button className="download-button" onClick={() => window.open(warfightingAcquisitionPDF, '_blank')}>
            <Download size={18} />
            <span>Download: Warfighting Acquisition System Memo (PDF)</span>
          </button>

          <h4>8. Department of War – Acquisition Transformation Strategy (10 November 2025)</h4>
          <p>
            <strong>Why it matters:</strong> The Acquisition Transformation Strategy translates the WAS memo into a detailed, multi-pillar implementation roadmap for the Department of War. It covers rebuilding the industrial base, empowering the acquisition workforce, increasing flexibility in tools and pathways, delivering high-performance systems, and improving lifecycle risk management. ALIGN℠ is designed to plug into this strategy as a practical framework for PAEs, program offices, and industry partners to execute portfolio-level roadmaps that meet WAS speed, rigor, and outcome expectations.
          </p>
          <p><strong>File name:</strong> Acquisition Transformation Strategy.pdf</p>
          <p><strong>Date:</strong> 10 November 2025</p>
          <button className="download-button" onClick={() => window.open(acquisitionTransformationPDF, '_blank')}>
            <Download size={18} />
            <span>Download: Acquisition Transformation Strategy (PDF)</span>
          </button>
          </div>
        </section>

        <section className="content-section">
          <h2>Connecting Policy to Practice with ALIGN℠</h2>
          <p>
            These documents collectively define the new rules of the game for Federal and Defense acquisition: portfolio accountability, AI-enabled decision-making, digital modernization, and industrial-base health. ALIGN℠ is intentionally engineered to sit on top of this policy stack and give leaders a practical way to:
          </p>
          <ul>
            <li>Translate strategy and memos into concrete portfolio roadmaps and execution plans</li>
            <li>Connect mission problems to funded solutions using Adaptive Roadmaps and Investment Horizons</li>
            <li>Apply the IAK model to move from pilot to production at scale</li>
            <li>Demonstrate measurable outcomes that satisfy WAS expectations for speed, rigor, and readiness</li>
          </ul>
        </section>
      </div>
    </div>
  );
}

export default Strategy;
